import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import {
  FormLogicEventBehaviour,
  FormLogicEventBehaviour_OnBlur,
  FormLogicEventBehaviour_OnChange,
  GenericTextFieldFormLogicPure,
} from "./generic";

import { SingleLineTextInput2023ViewModel } from "../ui2023";
import { useDataSourceCheckoutRegionsQuery_44 } from "../../providers/data-source-checkout-regions";
import { ACountry } from "../../model";

export interface ManualAddressExpects {
  addressLine1: SingleLineTextInput2023ViewModel;
  addressLine2: SingleLineTextInput2023ViewModel;
  suburb: SingleLineTextInput2023ViewModel;
  postcode: SingleLineTextInput2023ViewModel;
  state: SingleLineTextInput2023ViewModel;
  country: SingleLineTextInput2023ViewModel;
}

interface ManualAddressFormLogicPureProps {
  ref: React44ms.Api.TypedRef<ManualAddressExpects>;
  required?: boolean;
  shouldRestrictToCountry?: ACountry;
}
const ManualAddressFormLogicPure: React44ms.Api.FunctionComponent<
  ManualAddressFormLogicPureProps
> = (props) => {
  const { data: regions } = useDataSourceCheckoutRegionsQuery_44();

  const stateValueMustBeOneOf = React44ms.Api.useCallback(() => {
    return (regions?.available_regions || []).map((region) => region.code);
  }, [regions?.available_regions]);

  const countryValueMustBeOneOf = React44ms.Api.useCallback(
    () =>
      [ACountry.Australia, ACountry.NewZealand]
        .filter(
          (id) =>
            id !== ACountry.Singapore &&
            (!props.shouldRestrictToCountry ||
              id === props.shouldRestrictToCountry)
        )
        .map((id) => id),
    []
  );

  return (
    <form>
      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "addressLine1"
        )}
        required={props.required}
        maxLength={maxManualAddressLineLength}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour.validate,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
      />

      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "addressLine2"
        )}
        required={false}
        maxLength={maxManualAddressLineLength}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour.validate,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
      />

      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "suburb"
        )}
        required={props.required}
        maxLength={maxManualAddressLineLength}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour.validate,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
      />

      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "postcode"
        )}
        required={props.required}
        maxLength={maxPostcodeLength}
        customWarningValidation={postcode_customWarningValidation}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour.validate,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
      />

      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "state"
        )}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour_OnBlur.validateAsAnEffect,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
        required={true}
        valueMustBeOneOf={regions?.available_regions?.length ? stateValueMustBeOneOf : undefined}
      />

      <GenericTextFieldFormLogicPure
        ref={React44ms.Api.Convenience.element.name<ManualAddressExpects>(
          "country"
        )}
        behaviour={{
          onFocus: FormLogicEventBehaviour.nothing,
          onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
          onBlur: FormLogicEventBehaviour_OnBlur.validateAsAnEffect,
          onSubmit: FormLogicEventBehaviour.validate,
        }}
        required={props.required}
        valueMustBeOneOf={countryValueMustBeOneOf}
      />
    </form>
  );
};

export const ManualAddressFormLogic = React44ms.React.Reactify(
  ManualAddressFormLogicPure
);

const maxManualAddressLineLength = 32;
const maxPostcodeLength = 32;
const isAValidPostcode = (postcodeValue?: string) => {
  const isAValidPostcode = postcodeValue?.match(/^[0-9]{1,5}$/);
  return !!isAValidPostcode;
};
const POSTCODE_INVALID_CUSTOM_WARNING =
  "Provided Zip/Postal Code seems to be invalid. Example: 1234. If you believe it is the right one you can ignore this notice.";
const postcode_customWarningValidation = (value: any) => {
  if (!isAValidPostcode(value)) {
    return POSTCODE_INVALID_CUSTOM_WARNING;
  }
  return null;
};
